// src/pages/Clients.jsx
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useLayoutEffect,
  useCallback,
} from 'react';
import { createPortal } from 'react-dom';
import api from '../api/axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import AddClientModal from '../components/AddClientModal';
import EditClientModal from '../components/EditClientModal';

import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiSearch,
  FiFilter,
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
  FiArrowUp,
  FiArrowDown,
  FiDownload,
  FiColumns,
  FiMoreHorizontal,
  FiMail,
  FiUsers,
  FiCheckCircle,
  FiClock,
} from 'react-icons/fi';

import SavedViewsBar from '../components/clients/SavedViewsBar';
import ColumnManager from '../components/clients/ColumnManager';
import InlineCell from '../components/clients/InlineCell';

import {
  listClients,
  patchClient as svcPatchClient,
  updateClientField,
  bulkDeleteClients,
} from '../services/clientService';

import { handleDeleteItem } from '../utils/crmActions';

/* ----------------------------- helpers ----------------------------- */

const STORAGE_COLUMNS = 'hypercrm.clients.columns';
const DEFAULT_COLUMNS = [
  { id: 'name', label: 'Name', visible: true },
  { id: 'email', label: 'Email', visible: true },
  { id: 'phone', label: 'Phone', visible: true },
  { id: 'company', label: 'Company', visible: true },
  { id: 'tags', label: 'Tags', visible: true },
  { id: 'status', label: 'Status', visible: true },
  { id: 'actions', label: 'Actions', visible: true }, // hidden automatically for viewer role
];

const resolveId = (row) => row?.clientId || row?._id || row?.id;

function initials(name = '') {
  return (
    name
      .split(' ')
      .filter(Boolean)
      .map((w) => w[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || 'C'
  );
}

function statusChipClass(value) {
  switch (value) {
    case 'active':
      return 'bg-green-100 text-green-700 border border-green-200';
    case 'lead':
    case 'prospect':
      return 'bg-amber-100 text-amber-700 border border-amber-200';
    case 'inactive':
    case 'archived':
      return 'bg-gray-200 text-gray-700 border border-gray-300';
    case 'lost':
      return 'bg-rose-100 text-rose-700 border border-rose-200';
    default:
      return 'bg-blue-100 text-blue-700 border border-blue-200';
  }
}

const STATUS_OPTIONS = [
  { label: 'Active', value: 'active' },
  { label: 'Lead', value: 'lead' },
  { label: 'Prospect', value: 'prospect' },
  { label: 'Inactive', value: 'inactive' },
  { label: 'Archived', value: 'archived' },
  { label: 'Lost', value: 'lost' },
];

const emailValidate = (v) =>
  v && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? 'Invalid email' : null;
const phoneValidate = (v) =>
  v && !/^[\d+\-\s().]{5,}$/.test(v) ? 'Invalid phone' : null;

/* ----------------------------- portaled action menu ----------------------------- */

function ActionMenuPortal({
  anchorEl,
  row,
  canMutate,
  onClose,
  onView,
  onQuickEdit,
  onEmail,
  onDelete,
}) {
  const ref = useRef(null);
  const [style, setStyle] = useState({ top: 0, left: 0, origin: 'top-right' });

  useLayoutEffect(() => {
    if (!anchorEl) return;
    const r = anchorEl.getBoundingClientRect();
    const vh = window.innerHeight;

    const menuW = 220;
    const gap = 8;

    let left = r.right - menuW;
    if (left < 8) left = Math.max(8, r.left);

    let top = r.bottom + gap;
    let origin = 'top-right';
    setStyle((s) => ({ ...s, top, left, origin }));

    requestAnimationFrame(() => {
      const el = ref.current;
      if (!el) return;
      const h = el.offsetHeight;
      if (top + h > vh - 8) {
        top = Math.max(8, r.top - h - gap);
        origin = 'bottom-right';
      }
      setStyle({ top, left, origin });
    });
  }, [anchorEl]);

  useEffect(() => {
    const onDoc = (e) => {
      if (!ref.current) return;
      if (ref.current.contains(e.target)) return;
      if (anchorEl && anchorEl.contains(e.target)) return;
      onClose();
    };
    const onKey = (e) => e.key === 'Escape' && onClose();
    const onScrollOrResize = () => onClose();

    document.addEventListener('mousedown', onDoc, true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', onDoc, true);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [anchorEl, onClose]);

  const id = resolveId(row);

  return createPortal(
    <div
      ref={ref}
      role="menu"
      style={{ position: 'fixed', top: style.top, left: style.left, zIndex: 50 }}
      className={`min-w-[220px] rounded-xl border border-slate-200 bg-white shadow-xl origin-${style.origin} transform transition duration-100`}
    >
      <button
        role="menuitem"
        className="w-full px-3 py-2 text-left hover:bg-slate-50"
        onClick={() => {
          onClose();
          onView(id);
        }}
      >
        View details
      </button>

      <button
        role="menuitem"
        className="w-full px-3 py-2 text-left hover:bg-slate-50 flex items-center gap-2"
        onClick={() => {
          onClose();
          onQuickEdit(id);
        }}
      >
        <FiEdit2 /> Edit
      </button>

      <button
        role="menuitem"
        disabled={!row?.email}
        className={`w-full px-3 py-2 text-left flex items-center gap-2 ${
          !row?.email ? 'opacity-40 cursor-not-allowed' : 'hover:bg-slate-50'
        }`}
        onClick={() => {
          onClose();
          onEmail(row);
        }}
      >
        <FiMail /> Email
      </button>

      <div className="my-1 h-px bg-slate-100" />

      <button
        role="menuitem"
        disabled={!canMutate}
        className={`w-full px-3 py-2 text-left text-rose-600 ${
          !canMutate ? 'opacity-40 cursor-not-allowed' : 'hover:bg-rose-50'
        }`}
        onClick={() => {
          onClose();
          onDelete(row);
        }}
      >
        <FiTrash2 className="inline -mt-0.5 mr-1" /> Delete
      </button>
    </div>,
    document.body
  );
}

/* ----------------------------- bulk actions bar ----------------------------- */

const BulkActionsBar = ({
  selectedCount,
  onDeleteSelected,
  onBulkStatus,
  onBulkAddTag,
  onBulkRemoveTag,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [status, setStatus] = useState('active');
  const [tagText, setTagText] = useState('');

  return (
    <div className="fixed top-16 left-0 right-0 z-40 flex flex-wrap gap-3 items-center justify-between bg-blue-50/90 backdrop-blur border-b border-blue-200 px-6 py-3 shadow-sm">
      <span className="font-semibold text-blue-700">
        {selectedCount} client{selectedCount > 1 ? 's' : ''} selected
      </span>

      <div className="flex flex-wrap gap-2 items-center">
        {/* Change status */}
        <div className="flex items-center gap-2 bg-white rounded-lg border px-2 py-1">
          <span className="text-sm text-gray-600">Status:</span>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="text-sm bg-white appearance-none pr-6"
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
          <button
            onClick={() => onBulkStatus(status)}
            className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-blue-700"
          >
            Apply
          </button>
        </div>

        {/* Tags */}
        <div className="flex items-center gap-2 bg-white rounded-lg border px-2 py-1">
          <span className="text-sm text-gray-600">Tag:</span>
          <input
            value={tagText}
            onChange={(e) => setTagText(e.target.value)}
            placeholder="e.g. VIP"
            className="text-sm rounded border px-2 py-1"
          />
          <button
            onClick={() => onBulkAddTag(tagText)}
            className="rounded-md border px-3 py-1.5 text-sm hover:bg-gray-50"
          >
            Add
          </button>
          <button
            onClick={() => onBulkRemoveTag(tagText)}
            className="rounded-md border px-3 py-1.5 text-sm hover:bg-gray-50"
          >
            Remove
          </button>
        </div>

        {/* Delete */}
        <button
          className="bg-red-600 text-white px-3 py-2 rounded-lg hover:bg-red-700 font-semibold"
          onClick={onDeleteSelected}
        >
          <FiTrash2 className="inline mr-2 -mt-0.5" /> Delete
        </button>

        {/* Overflow (placeholder) */}
        <button
          onClick={() => setMenuOpen((v) => !v)}
          className="rounded-lg border bg-white px-3 py-2 text-sm hover:bg-gray-50"
          title="More actions"
        >
          <FiMoreHorizontal />
        </button>
        {menuOpen && (
          <div
            className="absolute right-6 top-full mt-2 w-56 rounded-xl border border-gray-200 bg-white shadow-lg"
            onMouseLeave={() => setMenuOpen(false)}
          >
            <div className="px-3 py-2 text-xs text-gray-500">Coming soon</div>
            <div className="px-3 pb-3 text-xs text-gray-400">
              Assign owner, Merge duplicates, Export selection
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ----------------------------- main ----------------------------- */

const Clients = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);

  const [userRole, setUserRole] = useState('viewer');
  const canMutate = userRole === 'admin' || userRole === 'staff';

  // filters / sort / search / pagination
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [tagFilter, setTagFilter] = useState('all');

  const [sortBy, setSortBy] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const [selected, setSelected] = useState([]);
  const [focusedId, setFocusedId] = useState(null);

  const [columns, setColumns] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_COLUMNS);
      const parsed = raw ? JSON.parse(raw) : DEFAULT_COLUMNS;
      return Array.isArray(parsed) ? parsed : DEFAULT_COLUMNS;
    } catch {
      return DEFAULT_COLUMNS;
    }
  });

  const [openColumnModal, setOpenColumnModal] = useState(false);

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuRow, setMenuRow] = useState(null);

  const searchRef = useRef(null);
  const navigate = useNavigate();

  /* ----------------------------- data ----------------------------- */

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      // Keeping compatibility with current service (fetch-all) while future-proofing params
      const resp = await listClients({
        // backend may ignore these; safe to pass
        search: undefined,
        status: undefined,
        page,
        limit: pageSize,
        sortBy,
        sortOrder: sortDir,
      });
      const items = Array.isArray(resp)
        ? resp
        : Array.isArray(resp?.items)
        ? resp.items
        : Array.isArray(resp?.data)
        ? resp.data
        : [];

      setClients(items);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      toast.error(e?.message || 'Failed to load clients');
      setClients([]);
    } finally {
      setLoading(false);
    }
    setSelected([]);
  }, [page, pageSize, sortBy, sortDir]);

  const fetchProfile = useCallback(async () => {
    try {
      const res = await api.get('/auth/profile');
      const r = res?.data?.data || res?.data;
      setUserRole((r?.role || 'viewer').toLowerCase());
      localStorage.setItem('profile', JSON.stringify(r || {}));
    } catch {
      try {
        const p = JSON.parse(localStorage.getItem('profile') || '{}');
        setUserRole((p?.role || 'viewer').toLowerCase());
      } catch {
        setUserRole('viewer');
      }
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  /* ----------------------------- saved views apply ----------------------------- */

  const applySavedView = (v) => {
    if (!v) return;
    setSearch(v.search ?? '');
    setStatusFilter(v.statusFilter ?? 'all');
    setTagFilter(v.tagFilter ?? 'all');
    setSortBy(v.sortBy ?? 'name');
    setSortDir(v.sortDir ?? 'asc');
    setPageSize(v.pageSize ?? 25);
    if (Array.isArray(v.columns)) {
      setColumns(v.columns);
      try {
        localStorage.setItem(STORAGE_COLUMNS, JSON.stringify(v.columns));
      } catch {}
    }
    setPage(1);
  };

  /* ----------------------------- derived ----------------------------- */

  const uniqueStatuses = useMemo(() => {
    const set = new Set(clients.map((c) => c.status).filter(Boolean));
    return ['all', ...Array.from(set)];
  }, [clients]);

  const uniqueTags = useMemo(() => {
    const set = new Set();
    clients.forEach(
      (c) => Array.isArray(c.tags) && c.tags.forEach((t) => set.add(String(t)))
    );
    return ['all', ...Array.from(set)];
  }, [clients]);

  // Client-side filter/sort/pagination (server params are passed but not required)
  const filteredClients = useMemo(() => {
    const q = search.trim().toLowerCase();
    return clients.filter((c) => {
      const matchesSearch =
        !q ||
        c.name?.toLowerCase().includes(q) ||
        c.company?.toLowerCase().includes(q) ||
        c.email?.toLowerCase().includes(q) ||
        c.phone?.toLowerCase().includes(q);

      const matchesStatus = statusFilter === 'all' || c.status === statusFilter;

      const matchesTag =
        tagFilter === 'all' ||
        (Array.isArray(c.tags) && c.tags.map((t) => String(t)).includes(tagFilter));

      return matchesSearch && matchesStatus && matchesTag;
    });
  }, [clients, search, statusFilter, tagFilter]);

  const sortedClients = useMemo(() => {
    const list = [...filteredClients];
    list.sort((a, b) => {
      const A = (a?.[sortBy] ?? '').toString().toLowerCase();
      const B = (b?.[sortBy] ?? '').toString().toLowerCase();
      if (A < B) return sortDir === 'asc' ? -1 : 1;
      if (A > B) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [filteredClients, sortBy, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sortedClients.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const startIdx = (currentPage - 1) * pageSize;
  const pageRows = sortedClients.slice(startIdx, startIdx + pageSize);

  // selection helpers
  const allFilteredIds = filteredClients.map((c) => resolveId(c));
  const isAllSelected =
    allFilteredIds.length > 0 && allFilteredIds.every((id) => selected.includes(id));
  const isPartialSelected = selected.length > 0 && !isAllSelected;

  const toggleSelectAll = () => {
    if (isAllSelected) setSelected([]);
    else setSelected(Array.from(new Set([...selected, ...allFilteredIds])));
  };
  const toggleRow = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  /* ----------------------------- row + bulk helpers ----------------------------- */

  // small wrapper: if you pass a single-key payload, we call updateClientField for safety
  const patchClient = async (id, payload) => {
    try {
      const keys = payload && typeof payload === 'object' ? Object.keys(payload) : [];
      if (keys.length === 1) {
        const field = keys[0];
        await updateClientField(id, field, payload[field]); // service helper
      } else {
        await svcPatchClient(id, payload); // generic patch if multiple fields
      }
      setClients((prev) =>
        prev.map((c) => (resolveId(c) === id ? { ...c, ...payload } : c))
      );
      toast.success('Saved');
    } catch (e) {
      toast.error(e?.message || 'Failed to save');
      throw e;
    }
  };

  const bulkPatch = async (payloadMapper) => {
    if (!selected.length) return;
    try {
      await Promise.all(
        selected.map((id) => {
          const client = clients.find((c) => resolveId(c) === id);
          const payload = payloadMapper(client);
          if (!payload) return Promise.resolve();
          return svcPatchClient(id, payload).catch(() => null);
        })
      );
      toast.success('Bulk update complete');
      fetchClients();
    } catch {
      toast.error('Bulk update failed');
    }
  };

  const onBulkStatus = (status) => bulkPatch(() => ({ status }));

  const onBulkAddTag = (tag) => {
    const t = (tag || '').trim();
    if (!t) return;
    bulkPatch((c) => {
      const next = Array.isArray(c.tags) ? Array.from(new Set([...c.tags, t])) : [t];
      return { tags: next };
    });
  };

  const onBulkRemoveTag = (tag) => {
    const t = (tag || '').trim();
    if (!t) return;
    bulkPatch((c) => {
      const next = Array.isArray(c.tags) ? c.tags.filter((x) => String(x) !== t) : [];
      return { tags: next };
    });
  };

  const handleBulkDelete = async () => {
    if (!window.confirm('Delete selected clients? This cannot be undone.')) return;
    const res = await bulkDeleteClients(selected);
    if (res?.errors?.length) {
      toast.error(`Some failed: ${res.errors.length}`);
    }
    toast.success(`Deleted ${res?.deleted || 0} client(s)`);
    fetchClients();
  };

  // export CSV (filtered list)
  const exportCSV = () => {
    const rows = filteredClients;
    if (!rows.length) return toast.info('Nothing to export.');
    const headers = ['Name', 'Email', 'Phone', 'Company,', 'Status', 'Tags'];
    const csv = [
      headers.join(','),
      ...rows.map((c) =>
        [
          c.name ?? '',
          c.email ?? '',
          c.phone ?? '',
          c.company ?? '',
          c.status ?? '',
          Array.isArray(c.tags) ? c.tags.join('|') : '',
        ]
          .map((v) => `"${String(v).replace(/"/g, '""')}"`)
          .join(',')
      ),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'clients.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const emailClient = (row) => {
    if (!row?.email) return;
    const subject = encodeURIComponent(`Hello ${row.name || ''}`);
    const body = encodeURIComponent('');
    window.location.href = `mailto:${row.email}?subject=${subject}&body=${body}`;
  };

  /* ----------------------------- keyboard shortcuts ----------------------------- */

  useEffect(() => {
    const handler = (e) => {
      const tag = (e.target?.tagName || '').toLowerCase();
      if (['input', 'textarea', 'select'].includes(tag)) return;

      if (e.key === 'f' || e.key === '/') {
        e.preventDefault();
        searchRef.current?.focus();
      }
      if (e.key === 'Escape') {
        setSelected([]);
        setFocusedId(null);
        setMenuAnchor(null);
        setMenuRow(null);
      }
      if (e.key === 'j' || e.key === 'k') {
        if (!pageRows.length) return;
        e.preventDefault();
        const idx = focusedId ? pageRows.findIndex((r) => resolveId(r) === focusedId) : -1;
        const nextIdx =
          e.key === 'j'
            ? Math.min(pageRows.length - 1, idx + 1)
            : Math.max(0, idx - 1);
        setFocusedId(resolveId(pageRows[nextIdx]));
      }
      if (e.key === 'o' && focusedId) navigate(`/clients/${focusedId}`);
      if (e.key === 'e' && focusedId) {
        const c = clients.find((x) => resolveId(x) === focusedId);
        if (c) setEditingClient(c);
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'a') {
        e.preventDefault();
        setSelected(allFilteredIds);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line
  }, [pageRows, focusedId, clients, allFilteredIds]);

  /* ----------------------------- UI ----------------------------- */

  const SortHeader = ({ id, label }) => {
    const active = sortBy === id;
    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          if (sortBy === id) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
          else {
            setSortBy(id);
            setSortDir('asc');
          }
          setPage(1);
        }}
        className={`inline-flex items-center gap-1 ${
          active ? 'text-gray-900' : 'text-gray-600 hover:text-gray-900'
        }`}
      >
        {label}
        {active ? (sortDir === 'asc' ? <FiArrowUp /> : <FiArrowDown />) : null}
      </button>
    );
  };

  const visibleColumns = useMemo(() => {
    const base = columns.filter((c) => c.visible);
    if (userRole === 'viewer') return base.filter((c) => c.id !== 'actions');
    return base;
  }, [columns, userRole]);

  const currentViewState = useMemo(
    () => ({
      search,
      statusFilter,
      tagFilter,
      sortBy,
      sortDir,
      pageSize,
      columns,
    }),
    [search, statusFilter, tagFilter, sortBy, sortDir, pageSize, columns]
  );

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_COLUMNS, JSON.stringify(columns));
    } catch {}
  }, [columns]);

  // Quick stats (lightweight, computed on current filtered set)
  const counts = useMemo(() => {
    const acc = { total: filteredClients.length, active: 0, leads: 0 };
    for (const c of filteredClients) {
      const s = String(c?.status || '').toLowerCase();
      if (s === 'active') acc.active++;
      if (s === 'lead' || s === 'prospect') acc.leads++;
    }
    return acc;
  }, [filteredClients]);

  return (
    <div className="w-full min-h-[90vh] py-8 px-3 sm:px-6 lg:px-8 bg-slate-50">
      {/* Header */}
      <div className="rounded-2xl mb-6 border border-slate-200 bg-white shadow-sm">
        <div className="p-6 sm:p-7">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-slate-900 text-white flex items-center justify-center">
                  <FiUsers />
                </div>
                <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Clients</h1>
              </div>
              <p className="text-slate-600 mt-1">
                Manage customers, contacts, and accounts. Inline edits, bulk actions, and CSV export supported.
              </p>
            </div>
            <div className="flex gap-2">
              {userRole !== 'viewer' && (
                <button
                  onClick={() => setShowModal(true)}
                  className="inline-flex items-center gap-2 bg-slate-900 text-white px-4 py-2 rounded-xl font-semibold shadow hover:bg-black/90 transition"
                >
                  <FiPlus className="text-lg" /> Add Client
                </button>
              )}
            </div>
          </div>

          {/* Quick Stats */}
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="rounded-xl border border-slate-200 p-3">
              <div className="text-slate-500 text-xs flex items-center gap-1">
                <FiUsers /> Filtered
              </div>
              <div className="text-lg font-bold text-slate-900">{counts.total}</div>
            </div>
            <div className="rounded-xl border border-slate-200 p-3">
              <div className="text-slate-500 text-xs flex items-center gap-1">
                <FiCheckCircle /> Active
              </div>
              <div className="text-lg font-bold text-slate-900">{counts.active}</div>
            </div>
            <div className="rounded-xl border border-slate-200 p-3">
              <div className="text-slate-500 text-xs flex items-center gap-1">
                <FiClock /> Leads/Prospects
              </div>
              <div className="text-lg font-bold text-slate-900">{counts.leads}</div>
            </div>
            <div className="rounded-xl border border-slate-200 p-3">
              <div className="text-slate-500 text-xs">Role</div>
              <div className="text-lg font-bold text-slate-900 capitalize">{userRole}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="mb-4">
        <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-4">
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
            <div className="relative w-full sm:w-96">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                ref={searchRef}
                type="text"
                placeholder="Search name, company, email, phone…"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="w-full pl-9 pr-3 py-2 text-sm border rounded-xl bg-white focus:outline-none focus:ring-4 focus:ring-blue-100"
              />
            </div>

            <div className="flex flex-1 sm:flex-none gap-2 justify-stretch sm:justify-end">
              {/* Status */}
              <div className="relative">
                <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                  <FiFilter />
                </div>
                <select
                  className="pl-9 pr-8 py-2 text-sm border rounded-xl bg-white appearance-none"
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(1);
                  }}
                  title="Filter by status"
                >
                  {uniqueStatuses.map((s) => (
                    <option key={s} value={s}>
                      {s === 'all' ? 'All Statuses' : s.charAt(0).toUpperCase() + s.slice(1)}
                    </option>
                  ))}
                </select>
                <FiChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" />
              </div>

              {/* Tags */}
              <div className="relative">
                <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                  <FiFilter />
                </div>
                <select
                  className="pl-9 pr-8 py-2 text-sm border rounded-xl bg-white appearance-none"
                  value={tagFilter}
                  onChange={(e) => {
                    setTagFilter(e.target.value);
                    setPage(1);
                  }}
                  title="Filter by tag"
                >
                  {uniqueTags.map((t) => (
                    <option key={t} value={t}>
                      {t === 'all' ? 'All Tags' : t}
                    </option>
                  ))}
                </select>
                <FiChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" />
              </div>

              {/* Sort / size */}
              <div className="relative">
                <select
                  className="pl-3 pr-8 py-2 text-sm border rounded-xl bg-white appearance-none"
                  value={sortBy}
                  onChange={(e) => {
                    setSortBy(e.target.value);
                    setPage(1);
                  }}
                  title="Sort by"
                >
                  {['name', 'company', 'email', 'phone', 'status'].map((s) => (
                    <option key={s} value={s}>
                      {s.charAt(0).toUpperCase() + s.slice(1)}
                    </option>
                  ))}
                </select>
                <FiChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" />
              </div>

              <div className="relative">
                <select
                  className="pl-3 pr-8 py-2 text-sm border rounded-xl bg-white appearance-none"
                  value={sortDir}
                  onChange={(e) => {
                    setSortDir(e.target.value);
                    setPage(1);
                  }}
                  title="Sort direction"
                >
                  <option value="asc">A → Z</option>
                  <option value="desc">Z → A</option>
                </select>
                <FiChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" />
              </div>

              <div className="relative">
                <select
                  className="pl-3 pr-8 py-2 text-sm border rounded-xl bg-white appearance-none"
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                  title="Rows per page"
                >
                  {[10, 25, 50, 100].map((n) => (
                    <option key={n} value={n}>
                      {n}/page
                    </option>
                  ))}
                </select>
                <FiChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" />
              </div>

              <button
                onClick={exportCSV}
                className="flex items-center gap-2 px-3 py-2 text-sm border rounded-xl bg-white hover:bg-gray-50"
                title="Export filtered list"
              >
                <FiDownload /> Export
              </button>

              <button
                onClick={() => setOpenColumnModal(true)}
                className="flex items-center gap-2 px-3 py-2 text-sm border rounded-xl bg-white hover:bg-gray-50"
                title="Manage columns"
              >
                <FiColumns /> Columns
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Saved Views */}
      <div className="mb-4">
        <SavedViewsBar current={currentViewState} onApply={applySavedView} />
      </div>

      {/* Bulk actions bar */}
      {selected.length > 0 && (
        <BulkActionsBar
          selectedCount={selected.length}
          onDeleteSelected={handleBulkDelete}
          onBulkStatus={onBulkStatus}
          onBulkAddTag={onBulkAddTag}
          onBulkRemoveTag={onBulkRemoveTag}
        />
      )}

      {/* Table Card */}
      <div className="w-full rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-100 text-sm">
            <thead className="sticky top-0 z-10 bg-slate-50/90 backdrop-blur">
              <tr className="text-xs uppercase tracking-wider text-slate-600">
                {canMutate && (
                  <th className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      ref={(input) => {
                        if (input) input.indeterminate = isPartialSelected;
                      }}
                      onChange={toggleSelectAll}
                    />
                  </th>
                )}

                {visibleColumns.map((col) => {
                  if (col.id === 'actions') {
                    return (
                      <th key={col.id} className="px-4 py-3 text-right font-bold">
                        Actions
                      </th>
                    );
                  }
                  const sortId =
                    col.id === 'tags'
                      ? null
                      : ['name', 'company', 'email', 'phone', 'status'].includes(col.id)
                      ? col.id
                      : null;

                  return (
                    <th key={col.id} className="px-4 py-3 text-left font-bold">
                      {sortId ? <SortHeader id={sortId} label={col.label} /> : col.label}
                    </th>
                  );
                })}
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100">
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i}>
                    <td
                      colSpan={visibleColumns.length + (canMutate ? 1 : 0)}
                      className="px-4 py-4"
                    >
                      <div className="h-4 w-full rounded bg-slate-100 animate-pulse" />
                    </td>
                  </tr>
                ))
              ) : pageRows.length === 0 ? (
                <tr>
                  <td
                    colSpan={visibleColumns.length + (canMutate ? 1 : 0)}
                    className="px-6 py-14"
                  >
                    <div className="text-center">
                      <div className="mx-auto mb-3 w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
                        <FiUsers />
                      </div>
                      <div className="text-slate-600 font-semibold">No clients found</div>
                      <div className="text-slate-500 text-sm mb-4">
                        Adjust filters or add a new client.
                      </div>
                      {canMutate && (
                        <button
                          onClick={() => setShowModal(true)}
                          className="inline-flex items-center gap-2 bg-slate-900 text-white px-4 py-2 rounded-xl font-semibold shadow hover:bg-black/90 transition"
                        >
                          <FiPlus /> Add Client
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                pageRows.map((client) => {
                  const id = resolveId(client);
                  const focused = focusedId === id;

                  return (
                    <tr
                      key={id}
                      className={`group transition ${focused ? 'bg-blue-50/40' : 'hover:bg-blue-50/40'}`}
                      onMouseEnter={() => setFocusedId(id)}
                    >
                      {canMutate && (
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selected.includes(id)}
                            onChange={() => toggleRow(id)}
                          />
                        </td>
                      )}

                      {visibleColumns.map((col) => {
                        if (col.id === 'name') {
                          return (
                            <td
                              key={col.id}
                              className="px-4 py-3 whitespace-nowrap"
                              onClick={() => navigate(`/clients/${id}`)}
                              title="View details"
                              style={{ cursor: 'pointer' }}
                            >
                              <div className="flex items-center gap-3">
                                <div className="h-8 w-8 rounded-full bg-blue-100 text-blue-700 border border-blue-200 flex items-center justify-center text-xs font-extrabold">
                                  {initials(client.name)}
                                </div>
                                <span className="font-semibold text-blue-900 group-hover:underline group-hover:text-blue-700">
                                  {client.name || '—'}
                                </span>
                              </div>
                            </td>
                          );
                        }

                        if (col.id === 'email') {
                          return (
                            <td
                              key={col.id}
                              className="px-4 py-3 whitespace-nowrap text-gray-700"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <InlineCell
                                value={client.email || ''}
                                type="email"
                                validate={emailValidate}
                                onSave={(val) => patchClient(id, { email: val })}
                              />
                            </td>
                          );
                        }

                        if (col.id === 'phone') {
                          return (
                            <td
                              key={col.id}
                              className="px-4 py-3 whitespace-nowrap text-gray-700"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <InlineCell
                                value={client.phone || ''}
                                type="tel"
                                validate={phoneValidate}
                                onSave={(val) => patchClient(id, { phone: val })}
                              />
                            </td>
                          );
                        }

                        if (col.id === 'company') {
                          return (
                            <td key={col.id} className="px-4 py-3 whitespace-nowrap text-gray-700">
                              {client.company || '—'}
                            </td>
                          );
                        }

                        if (col.id === 'tags') {
                          return (
                            <td key={col.id} className="px-4 py-3 whitespace-nowrap">
                              <div className="flex flex-wrap gap-1" onClick={(e) => e.stopPropagation()}>
                                {Array.isArray(client.tags) && client.tags.length > 0 ? (
                                  client.tags.map((tag, idx) => (
                                    <span
                                      key={idx}
                                      className="bg-blue-50 text-blue-700 border border-blue-100 px-2 py-0.5 rounded-full text-xs font-semibold"
                                    >
                                      {tag}
                                    </span>
                                  ))
                                ) : (
                                  <span className="text-gray-300 text-xs">—</span>
                                )}
                              </div>
                            </td>
                          );
                        }

                        if (col.id === 'status') {
                          return (
                            <td
                              key={col.id}
                              className="px-4 py-3 whitespace-nowrap"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <InlineCell
                                value={client.status || ''}
                                type="select"
                                options={STATUS_OPTIONS}
                                onSave={(val) => patchClient(id, { status: val })}
                              />
                            </td>
                          );
                        }

                        if (col.id === 'actions') {
                          return canMutate ? (
                            <td
                              key={col.id}
                              className="px-4 py-3 whitespace-nowrap"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="flex justify-end">
                                <button
                                  className="p-2 rounded hover:bg-slate-100"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setMenuRow(client);
                                    setMenuAnchor(e.currentTarget);
                                  }}
                                  aria-haspopup="menu"
                                  aria-expanded={menuRow && resolveId(menuRow) === id ? 'true' : 'false'}
                                  title="Actions"
                                >
                                  <FiMoreHorizontal />
                                </button>
                              </div>
                            </td>
                          ) : (
                            <td key={col.id} className="px-4 py-3 whitespace-nowrap text-right">
                              <button
                                className="px-3 py-1.5 rounded-xl border text-sm"
                                onClick={() => navigate(`/clients/${id}`)}
                              >
                                View
                              </button>
                            </td>
                          );
                        }

                        return null;
                      })}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden divide-y divide-slate-100">
          {loading ? (
            <div className="p-6 space-y-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-16 rounded-xl bg-slate-100 animate-pulse" />
              ))}
            </div>
          ) : pageRows.length === 0 ? (
            <div className="p-6 text-center text-slate-500">No clients found.</div>
          ) : (
            pageRows.map((client) => {
              const id = resolveId(client);
              return (
                <div key={id} className="p-4 hover:bg-blue-50/60 transition" onClick={() => navigate(`/clients/${id}`)}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-blue-100 text-blue-700 border border-blue-200 flex items-center justify-center text-sm font-extrabold">
                          {initials(client.name)}
                        </div>
                        <div className="min-w-0">
                          <div className="font-semibold text-slate-900 truncate">
                            {client.name || '—'}
                          </div>
                          <div className="text-sm text-slate-600 truncate">{client.company || '—'}</div>
                        </div>
                      </div>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {Array.isArray(client.tags) && client.tags.length > 0 ? (
                          client.tags.map((tag, idx) => (
                            <span
                              key={idx}
                              className="bg-blue-50 text-blue-700 border border-blue-100 px-2 py-0.5 rounded-full text-xs font-semibold"
                            >
                              {tag}
                            </span>
                          ))
                        ) : (
                          <span className="text-gray-300 text-xs">—</span>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`ml-2 px-2 py-0.5 rounded text-xs font-semibold ${statusChipClass(client.status)}`}>
                        {client.status || '—'}
                      </span>
                    </div>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      className="px-3 py-1.5 rounded-xl border text-sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/clients/${id}`);
                      }}
                    >
                      View
                    </button>

                    {canMutate ? (
                      <>
                        <button
                          className="px-3 py-1.5 rounded-xl border text-sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingClient(client);
                          }}
                        >
                          <FiEdit2 className="inline -mt-0.5 mr-1" />
                          Edit
                        </button>

                        <button
                          className={`px-3 py-1.5 rounded-xl border text-sm ${!client?.email ? 'opacity-40 cursor-not-allowed' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (client?.email) emailClient(client);
                          }}
                          disabled={!client?.email}
                        >
                          <FiMail className="inline -mt-0.5 mr-1" />
                          Email
                        </button>

                        <button
                          className="px-3 py-1.5 rounded-xl border text-sm text-rose-600 border-rose-200"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm('Delete this client?')) {
                              handleDeleteItem('clients', id, fetchClients);
                            }
                          }}
                        >
                          <FiTrash2 className="inline -mt-0.5 mr-1" />
                          Delete
                        </button>
                      </>
                    ) : null}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Pagination */}
        <div className="px-4 py-3 bg-slate-50 border-t flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="text-sm text-slate-600">
            Showing <span className="font-semibold">{pageRows.length}</span> of{' '}
            <span className="font-semibold">{sortedClients.length}</span> filtered clients
            {filteredClients.length !== clients.length && <> (from {clients.length} total)</>}
          </div>
          <div className="flex items-center gap-2">
            <button
              className="p-2 rounded-xl border bg-white disabled:opacity-40"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={currentPage <= 1 || loading}
              title="Previous"
            >
              <FiChevronLeft />
            </button>
            <span className="text-sm text-slate-700">
              Page <span className="font-semibold">{currentPage}</span> of{' '}
              <span className="font-semibold">{totalPages}</span>
            </span>
            <button
              className="p-2 rounded-xl border bg-white disabled:opacity-40"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages || loading}
              title="Next"
            >
              <FiChevronRight />
            </button>
          </div>
        </div>
      </div>

      {/* Portaled action menu */}
      {menuAnchor && menuRow && (
        <ActionMenuPortal
          anchorEl={menuAnchor}
          row={menuRow}
          canMutate={canMutate}
          onClose={() => {
            setMenuAnchor(null);
            setMenuRow(null);
          }}
          onView={(id) => navigate(`/clients/${id}`)}
          onQuickEdit={(id) => {
            const c = clients.find((r) => resolveId(r) === id);
            if (c) setEditingClient(c);
          }}
          onEmail={emailClient}
          onDelete={(row) => {
            const id = resolveId(row);
            if (window.confirm('Delete this client?')) {
              handleDeleteItem('clients', id, fetchClients);
            }
          }}
        />
      )}

      {/* Modals */}
      {openColumnModal && (
        <ColumnManager
          columns={columns}
          onChange={(next) => setColumns(next)}
          onClose={() => setOpenColumnModal(false)}
          storageKey={STORAGE_COLUMNS}
        />
      )}

      {showModal && (
        <AddClientModal onClose={() => setShowModal(false)} onSuccess={fetchClients} />
      )}

      {editingClient && (
        <EditClientModal
          client={editingClient}
          onClose={() => setEditingClient(null)}
          onSuccess={() => {
            setEditingClient(null);
            fetchClients();
          }}
        />
      )}
    </div>
  );
};

export default Clients;
